import { ApiKey, GTagId } from '@paid-search/core/src/domain';
import { IntegrationData } from './index';
import { HotelGTagIds } from '@paid-search/core/src/accountHotel/HotelGTagIds';
import { Rollbar } from './rollbar/MiniRollbar';
import { JSONableErrorWrapper } from '@paid-search/core/src/error';
import { UnableToFindGTagIdsForAnApiKey } from '@paid-search/public-api/src/accountHotel/HotelGTagIdsInPublicApi';
import { asError } from 'catch-unknown';
import { RoomsAndRatesPage } from './onboard/RoomsAndRatesPage';

export class App {
  constructor(
    private readonly window: Window,
    private readonly hotelGTagIds: HotelGTagIds,
    private readonly createGTag: () => void,
    private readonly rollbar: Rollbar,
  ) {}

  async withIntegrationData(integrationData: IntegrationData) {
    try {
      let apiKey: ApiKey;
      if (integrationData.apiKey) {
        apiKey = new ApiKey(integrationData.apiKey);
      } else {
        return;
      }

      const gTagIds = await this.getGTagIds(apiKey);

      if (gTagIds.length === 0) {
        return;
      }

      this.addGoogleAdsScript(gTagIds[0]);

      if (integrationData.type !== 'Payment') {
        gTagIds.forEach((gTagId) => {
          this.initialiseGTagId(gTagId);
          const { formattedCheckin, formattedDayOfWeek } = this.formatAudienceDataInfo(integrationData);

          this.window.gtag('event', 'page_view', {
            send_to: gTagId.value,
            api_key: integrationData.apiKey,
            client_key: integrationData.clientKey,
            event: integrationData.type,
            checkin: formattedCheckin,
            checkin_day_of_week: formattedDayOfWeek,
          });
        });
      }
    } catch (e) {
      this.rollbar.error(asError(e));
    }
  }

  private formatAudienceDataInfo(integrationData: IntegrationData) {
    const checkin = (integrationData as RoomsAndRatesPage)?.checkin;
    const formattedCheckin = typeof checkin?.toISOString === 'function' ? checkin?.toISOString().split('T')[0] : undefined;
    const formattedDayOfWeek =
      typeof checkin?.getTime === 'function' && !isNaN(checkin?.getTime())
        ? new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(checkin)
        : undefined;
    return { formattedCheckin, formattedDayOfWeek };
  }

  private initialiseGTagId(gTagId: GTagId) {
    this.window.gtag('config', gTagId.value, { send_page_view: false });
  }

  private async getGTagIds(apiKey: ApiKey): Promise<GTagId[]> {
    let gTagIds: GTagId[] = [];

    try {
      gTagIds = await this.hotelGTagIds.findAll(apiKey);
    } catch (e) {
      const error = JSONableErrorWrapper.from(e);
      if (
        error instanceof UnableToFindGTagIdsForAnApiKey &&
        error.innerError.name === 'TypeError' &&
        error.innerError.message === 'Failed to fetch'
      ) {
        this.rollbar.info(error.message, error);
      } else throw e;
    }

    return gTagIds;
  }

  private addGoogleAdsScript(gTagId: GTagId) {
    if (!this.window.gtag) {
      const gtmScript = this.window.document.createElement('script');
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtag/js?id=${gTagId.value}`;
      this.window.document.head.appendChild(gtmScript);

      this.createGTag();
    }

    this.window.gtag('js', new Date());
  }
}
